import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { batchElementId } from '@aytm/app/stats/page/components/lib/utils'
import { calcUncollapsedKeys } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'

import ItemsList from '@aytm/app/stats/page/question-views/components/items-list/ItemsList'
import { pressable } from '@aytm/app/stats/components/lib/pressable'
import { isCollapsable, ItemBody, ItemHeader } from '@aytm/app/stats/page/question-views/oex/components/Item'

import style from '@aytm/app/stats/page/question-views/oex/components/styles/Items.module.scss'

// Which variants the list actually draws: an extended open-ended shows every one, a plain
// answer-variants block only the ones carrying verbatims. Exported so a caller can ask whether the
// list would draw anything without duplicating the test.
export const answerVariantItems = (answerVariants, type) =>
  (answerVariants || []).filter(item => item && ('oex' === type || item.comments))

// The answer-variant list of an extended open-ended question: one collapsible row per variant,
// each expanding to that variant's own open-ended block. Replaces
// open_ended_extended/answer_variants.coffee and the sub-item view under it.
//
// Controlled, like every other sub-items list: which variants are open lives with the shell,
// which owns a set of report params per open variant and supplies its resolved `oeProps`.
const Items = ({
  questionId,
  answerVariants,
  type,
  answerLabels,
  uncollapsed,
  expandable = true,
  oePropsOf,
  onToggle,
  onToggleAll
}) => {
  const items = useMemo(() => answerVariantItems(answerVariants, type), [answerVariants, type])

  const collapsable = useMemo(() => items.filter(item => isCollapsable(item, type)), [items, type])
  const openKeys = useMemo(() => calcUncollapsedKeys(uncollapsed), [uncollapsed])

  const allOpen = 0 < collapsable.length && openKeys.length === collapsable.length

  const onToggleAllClick = () => onToggleAll(allOpen)

  return (
    <>
      {1 < collapsable.length ? (
        <div className={style.toggleRow}>
          <span className={style.toggleAll} aria-expanded={allOpen} {...pressable(onToggleAllClick)}>
            {allOpen ? 'Collapse answers' : 'Expand answers'}
          </span>
        </div>
      ) : null}

      <ItemsList
        items={items}
        itemKey={item => item.position}
        itemId={item => batchElementId(questionId, item.position)}
        openKeys={openKeys}
        renderHeader={(item, { collapsed }) => (
          <ItemHeader
            item={item}
            type={type}
            answerLabels={answerLabels}
            collapsed={collapsed}
            expandable={expandable}
          />
        )}
        renderBody={item => <ItemBody oeProps={oePropsOf?.(item)} />}
        onToggle={onToggle}
      />
    </>
  )
}

Items.displayName = 'OexItems'

Items.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  answerVariants: PropTypes.array,
  type: PropTypes.string,
  answerLabels: PropTypes.string,
  uncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  expandable: PropTypes.bool,
  // (item) => the open-ended block's own props bundle, or null while it is collapsed
  oePropsOf: PropTypes.func,
  onToggle: PropTypes.func,
  onToggleAll: PropTypes.func
}

export default Items
